// SQP-302 Phase 0: the ONE status vocabulary (docs/DASHBOARD-DESIGN.md section 3). Pure; no React.
//
// Five roles. Red (`critical`) means "broken, fix it" and NOTHING ELSE: a policy decline, a close, a
// deliberate pause or a no-flight-options outcome is an OUTCOME, not an error, and is `neutral`.
// The brand tint is never a status; it means interactive/selected.
//
// Class strings are written out in full (not built) so Tailwind's content scan sees them.

export const ROLES = ['ok', 'attention', 'critical', 'info', 'neutral'];

export const ROLE_META = {
  ok:        { icon: 'check-circle',      pillClass: 'bg-ok-fill text-ok' },
  attention: { icon: 'warning',           pillClass: 'bg-attention-fill text-attention' },
  critical:  { icon: 'x-octagon',         pillClass: 'bg-critical-fill text-critical' },
  info:      { icon: 'arrows-clockwise',  pillClass: 'bg-info-fill text-info' },
  neutral:   { icon: 'minus-circle',      pillClass: 'bg-neutral-fill text-neutral' },
};

// Normalised value (lower-case, whitespace/underscore/dash collapsed to single spaces) -> role.
const BY_VALUE = {
  // ok: done or healthy, no action
  'running': 'ok', 'healthy': 'ok', 'posted': 'ok', 'sent': 'ok', 'approved sent': 'ok',
  'booked': 'ok', 'quotes added': 'ok', 'connected': 'ok', 'live': 'ok',
  // attention: a human should look
  'held': 'attention', 'stalled': 'attention', 'pending review': 'attention', 'pending': 'attention',
  'verdict changed': 'attention', 'crew data unreadable': 'attention',
  'paused without audit record': 'attention', 'ready to send': 'attention',
  // critical: broken, fix it
  'down': 'critical', 'send failed': 'critical', 'error': 'critical', 'unhealthy': 'critical',
  'stuck': 'critical', 'retry loop': 'critical', 'tz guard wrong': 'critical', 'disconnected': 'critical',
  // info: the system will re-check, informational
  'recheck': 'info', 'sleeping': 'info', 'off hours': 'info',
  // neutral: closed or by choice
  'declined': 'neutral', 'no flight options': 'neutral', 'policy close': 'neutral', 'closed': 'neutral',
  'do not post': 'neutral', 'paused': 'neutral', 'stale': 'neutral', 'auto resolved': 'neutral',
  'cleared': 'neutral', 'duplicate already sent': 'neutral',
  // normal terminal outcomes under other names: an outcome is never "broken"
  'phase3 close': 'neutral', 'cancelled': 'neutral', 'canceled': 'neutral',
  'over cap': 'neutral', 'no availability': 'neutral',
};

const norm = v => String(v == null ? '' : v)
  .toLowerCase()
  .replace(/[→>]/g, ' ')
  .replace(/[()]/g, ' ')
  .replace(/[_\-\s]+/g, ' ')
  .trim();

// Old server/page tone names -> role. `teal` is deliberately absent: tint is not a status.
export const roleForTone = tone =>
  ({ green: 'ok', amber: 'attention', red: 'critical', gray: 'neutral' })[tone] || null;

// Role -> badge class (literal strings so Tailwind's content scan keeps the @layer components rules).
export const BADGE_CLASS = {
  ok: 'badge-ok',
  attention: 'badge-attention',
  critical: 'badge-critical',
  info: 'badge-info',
  neutral: 'badge-neutral',
};

// The badge class for a legacy server tone. `teal` is not a status and keeps its tint treatment;
// anything unrecognised is neutral (never red).
export const badgeClassForTone = tone => {
  const role = roleForTone(tone);
  return role ? BADGE_CLASS[role] : tone === 'teal' ? 'badge-teal' : 'badge-neutral';
};

// statusFor(kind, value, opts) -> { role, icon, pillClass, known }
//   kind  : 'verdict' | 'disposition' | 'operator' | 'process' | 'trip' | 'phase2' | ... (free text;
//           only 'verdict' changes behaviour today).
//   opts.resolved : for verdicts SEND / ALREADY_LEGAL: ok when resolved, attention while awaiting review.
// An unrecognised value is `attention` with known:false: a human should look at what we cannot
// classify, and it must never be silently painted ok or critical.
export function statusFor(kind, value, opts = {}) {
  const v = norm(value);
  let role = null;
  if (kind === 'verdict' && (v === 'send' || v === 'already legal')) {
    role = opts.resolved ? 'ok' : 'attention';
  } else if (kind === 'verdict' && v === 'recheck') {
    role = 'info';
  } else if (Object.prototype.hasOwnProperty.call(BY_VALUE, v)) {
    role = BY_VALUE[v];
  }
  const known = role !== null;
  if (!known) role = 'attention';
  return { role, known, icon: ROLE_META[role].icon, pillClass: ROLE_META[role].pillClass };
}

// For tests and documentation: every normalised value this module classifies.
export const KNOWN_VALUES = Object.freeze({ ...BY_VALUE });
